/**
 * RegistryConnectModal
 *
 * Guides the user through entering their API token for an authenticated registry MCP.
 * On success it shows them their personal MCP URL with a copy button.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, KeyRound, Copy, Check, ExternalLink, Shield, AlertTriangle, Loader2 } from "lucide-react";
import type { SessionUser } from "../lib/sessionUser";
import { connectRegistryMcp } from "../lib/registryApi";
import type { RegistryDetails } from "../lib/registryApi";

type Props = {
  mcp: Pick<RegistryDetails, "slug" | "name" | "icon" | "authLabel" | "authInstructions" | "credentialKey">;
  user: SessionUser;
  onClose: () => void;
  onConnected?: (mcpUrl: string) => void;
};

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label="Copy"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted/70 hover:bg-muted text-muted-foreground hover:text-foreground text-xs font-medium transition-colors"
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <motion.span key="check" initial={{ scale: 0.7 }} animate={{ scale: 1 }} exit={{ scale: 0.7 }} className="flex items-center gap-1">
            <Check className="w-3 h-3 text-emerald-500" /> Copied!
          </motion.span>
        ) : (
          <motion.span key="copy" initial={{ scale: 0.7 }} animate={{ scale: 1 }} exit={{ scale: 0.7 }} className="flex items-center gap-1">
            <Copy className="w-3 h-3" /> Copy
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

export default function RegistryConnectModal({ mcp, user, onClose, onConnected }: Props) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mcpUrl, setMcpUrl] = useState<string | null>(null);

  const handleConnect = async () => {
    const t = token.trim();
    if (!t) return;
    setLoading(true);
    setError(null);
    try {
      const result = await connectRegistryMcp(mcp.slug, t, user);
      setMcpUrl(result.mcpUrl);
      onConnected?.(result.mcpUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connection failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        key="modal"
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        transition={{ duration: 0.2 }}
        className="relative z-10 w-full max-w-lg bg-background border border-border rounded-2xl shadow-2xl p-7"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-modal-title"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {!mcpUrl ? (
          /* ─ Step 1: enter token ─ */
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center text-2xl flex-shrink-0">
                {mcp.icon || "🔑"}
              </div>
              <div>
                <h2 id="connect-modal-title" className="text-lg font-bold text-foreground">
                  Connect {mcp.name}
                </h2>
                <p className="text-sm text-muted-foreground">
                  Enter your {mcp.authLabel ?? "API token"} to get a personal MCP URL.
                </p>
              </div>
            </div>

            {mcp.authInstructions && (
              <div className="mb-5 text-sm text-muted-foreground bg-muted/40 rounded-xl px-4 py-3 border border-border/60 leading-relaxed">
                {mcp.authInstructions}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="token-input" className="block text-sm font-semibold text-foreground mb-2">
                  {mcp.authLabel ?? "API Token"}
                </label>
                <input
                  id="token-input"
                  type="password"
                  autoComplete="off"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && token.trim()) void handleConnect(); }}
                  placeholder={`Paste your ${mcp.credentialKey ?? "token"} here…`}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm font-mono text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring/30 transition"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl px-4 py-3">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                Your token is encrypted with AES-256 before storage and never exposed in any URL or response.
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  type="button"
                  onClick={() => void handleConnect()}
                  disabled={loading || !token.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none transition-all"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</>
                  ) : (
                    <><KeyRound className="w-4 h-4" /> Connect & Get URL</>
                  )}
                </motion.button>
              </div>
            </div>
          </>
        ) : (
          /* ─ Step 2: show personal URL ─ */
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 id="connect-modal-title" className="text-lg font-bold text-foreground">
                  Connected!
                </h2>
                <p className="text-sm text-muted-foreground">
                  Your personal {mcp.name} MCP URL is ready.
                </p>
              </div>
            </div>

            {/* Personal URL */}
            <div className="mb-5">
              <p className="text-sm font-semibold text-foreground mb-2">Your Personal MCP URL</p>
              <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-xl px-4 py-3">
                <code className="flex-1 text-xs font-mono text-foreground break-all leading-relaxed">{mcpUrl}</code>
                <CopyButton text={mcpUrl} />
                <a
                  href={mcpUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Open in new tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            {/* Claude Desktop snippet */}
            <div className="mb-5">
              <p className="text-sm font-semibold text-foreground mb-2">Add to Claude Desktop</p>
              <p className="text-xs text-muted-foreground mb-3">
                Open <code className="bg-muted px-1 rounded">~/Library/Application Support/Claude/claude_desktop_config.json</code> and add:
              </p>
              <div className="relative">
                <pre className="text-xs font-mono bg-background border border-border rounded-xl p-4 overflow-x-auto text-foreground leading-relaxed">
{`{
  "mcpServers": {
    "${mcp.slug}": {
      "url": "${mcpUrl}"
    }
  }
}`}
                </pre>
                <div className="absolute top-2 right-2">
                  <CopyButton text={`{\n  "mcpServers": {\n    "${mcp.slug}": {\n      "url": "${mcpUrl}"\n    }\n  }\n}`} />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50/70 dark:bg-amber-950/30 rounded-xl px-4 py-3 border border-amber-200 dark:border-amber-900 mb-5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Keep this URL private — it authenticates as you. You can revoke it anytime from your dashboard Integrations tab.</span>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-full px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Done
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
